import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { EmbalagemService } from '../../../shared/services/apis/produto/embalagem.service';
import { EmbalagemStruct } from '../../../shared/services/structs/produto/embalagem.struct';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { TipoEmbalagemEnum } from '../../../shared/enums/produto/tipo-embalagem.enum';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';

export interface EmbalagemListViewModel {
  idEmbalagem: number;
  descricao: string;
  tipo: string;
  dimensoes: string;
  peso: string;
}

@Component({
  selector: 'app-embalagem-lista',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './embalagem-lista.component.html',
  styleUrl: './embalagem-lista.component.css',
})
export class EmbalagemListaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Embalagens;
  public model: FormGroup;
  // Pagination state
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<EmbalagemListViewModel>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'descricao', 'tipo', 'dimensoes', 'peso', 'actions'];

  public selection = new SelectionModel<EmbalagemListViewModel>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private embalagemService: EmbalagemService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarEmbalagens();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/cadastros/embalagem/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  private carregarEmbalagens(reset: boolean = false): void {
    this._isLoading = true;
    const descricao = this.model.get('pesquisa')?.value || '';

    this.embalagemService.getAll(descricao, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar embalagens',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;
        const viewModels = response.listaEmbalagens.map(emb => this.mapToViewModel(emb));

        if (reset) {
          this._dataSource.data = viewModels;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...viewModels];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar embalagens',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarEmbalagens();
  }

  private mapToViewModel(emb: EmbalagemStruct): EmbalagemListViewModel {
    const fmt2 = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    let dimensoes = '-';

    switch (emb.idTipoEmbalagem) {
      case TipoEmbalagemEnum.PacoteCaixa:
        if (emb.larguraCm != null && emb.alturaCm != null && emb.comprimentoCm != null) {
          dimensoes = `${fmt2(emb.larguraCm)} x ${fmt2(emb.comprimentoCm)} x ${fmt2(emb.alturaCm)} cm`;
        }
        break;
      case TipoEmbalagemEnum.RoloCilindro:
        if (emb.diametroCm != null && emb.comprimentoCm != null) {
          dimensoes = `Ø ${fmt2(emb.diametroCm)} x ${fmt2(emb.comprimentoCm)} cm`;
        }
        break;
      case TipoEmbalagemEnum.Envelope:
        if (emb.larguraCm != null && emb.comprimentoCm != null) {
          dimensoes = `${fmt2(emb.larguraCm)} x ${fmt2(emb.comprimentoCm)} cm`;
        }
        break;
      default:
        // Fallback to existing logic if type is unknown
        if (emb.larguraCm != null && emb.alturaCm != null && emb.comprimentoCm != null) {
          dimensoes = `${fmt2(emb.larguraCm)} x ${fmt2(emb.comprimentoCm)} x ${fmt2(emb.alturaCm)} cm`;
        } else if (emb.diametroCm != null) {
          dimensoes = `Ø ${fmt2(emb.diametroCm)} cm`;
        }
        break;
    }

    const peso = emb.pesoKg != null
      ? `${emb.pesoKg.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`
      : '-';

    return {
      idEmbalagem: emb.idEmbalagem,
      descricao: emb.descricao,
      tipo: emb.tipoEmbalagemDescricao,
      dimensoes: dimensoes,
      peso: peso,
    };
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = []; // Reset data
    this.selection.clear(); // Bug 3: sincroniza seleção com nova lista
    this.carregarEmbalagens(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: EmbalagemListViewModel): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const title = isBulk ? 'Excluir Embalagens' : 'Excluir Embalagem';
    const description = isBulk
      ? `Deseja realmente excluir as ${itemsToDelete.length} embalagens selecionadas? Ao excluir embalagens, as configurações realizadas nos produtos serão removidas.`
      : `Deseja realmente excluir a embalagem "${item?.descricao}"? Ao excluir embalagens, as configurações realizadas nos produtos serão removidas.`;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idEmbalagem);
          this.embalagemService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir itens: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Embalagens excluídas com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar(); // Reload list
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir embalagens',
                AlertType.error
              );
            },
          });
        } else {
          // Single delete
          this.embalagemService.delete(item!.idEmbalagem).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir embalagem',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Embalagem excluída com sucesso', AlertType.success);
                this.clickPesquisar(); // Reload list
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir embalagem',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }

  public imprimir(): void {
    console.log('Imprimir');
  }
}
